import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL;

// =========================
// 🔹 TOKEN HELPERS
// =========================
const getAccessToken = () => localStorage.getItem("token");
const setAccessToken = (token) => localStorage.setItem("token", token);
const getRefreshToken = () => localStorage.getItem("refreshToken");
const clearSession = () => {
  console.warn("⚠️ Token invalid or session expired — clearing local storage.");
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user");
  localStorage.removeItem("cartItems");
  window.location.href = "/login";
};

// =========================
// 🔹 AXIOS INSTANCE
// =========================
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

// Attach token to every request
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// =========================
// 🔹 AUTO TOKEN REFRESH
// =========================
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only handle 401 once per request
    if (error?.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // If another refresh is in progress, queue the request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        const refreshToken = getRefreshToken();
        if (!refreshToken) {
          clearSession();
          return Promise.reject(error);
        }

        // Request new access token
        const res = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken,
        });

        const newToken = res.data.accessToken;
        if (!newToken) throw new Error("No new token returned.");

        setAccessToken(newToken);
        api.defaults.headers.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);

        // Retry the failed request
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        processQueue(refreshError, null);
        clearSession();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// =========================
// 🔹 CART SERVICES
// =========================
export const fetchCart = async () => {
  try {
    const response = await api.get("/cart");
    return response.data.items || [];
  } catch (error) {
    console.error("Error fetching cart:", error);
    return [];
  }
};

export const addToCart = async (productId, quantity = 1, variant = {}) => {
  try {
    await api.post("/cart/add", { productId, quantity, variant });
  } catch (error) {
    console.error("Error adding to cart:", error);
    throw error;
  }
};

export const updateCartQuantity = async (productId, quantity, variant = {}) => {
  try {
    await api.put("/cart/update", { productId, quantity, variant });
  } catch (error) {
    console.error("Error updating cart quantity:", error);
    throw error;
  }
};

export const removeFromCart = async (productId, variant = {}) => {
  try {
    await api.delete("/cart/remove", { data: { productId, variant } });
  } catch (error) {
    console.error("Error removing from cart:", error);
    throw error;
  }
};

export const clearCart = async () => {
  try {
    await api.delete("/cart/clear");
  } catch (error) {
    console.error("Error clearing cart:", error);
    throw error;
  }
};
